import React, { useState } from "react";
import classNames from "classnames";
import pluralize from "pluralize";
import { ApolloCache } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faExclamationTriangle,
} from "@fortawesome/pro-solid-svg-icons";

import Button from "ui/Button";
import SingleSelect, { StylesConfig } from "ui/SingleSelect";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import SectionHeading from "ui/SectionHeading";
import ModalContainer from "ui/ModalContainer";
import { AlertDestinationDetailModal } from "features/Checks/AlertDestinationDetail";
import { TargetTag } from "features/Checks/AlertDestinationTag";
import { reactSelectStyles } from "ui/CreatableSelect";
import CheckStatusBadge from "features/Checks/CheckStatusBadge";
import { CHECK_INFO } from "features/Checks/CheckList";
import { useRoutes } from "utils/routes";
import { useGoBackOr } from "utils/hooks";
import { sameSet } from "utils/array";
import { useAsyncActionFlash } from "providers/WithFlashes";
import { checkMaxSeverity } from "../../../../../docs/util/checks";
import { getCheckDescription } from "../../../../../docs/components/CheckDocumentation";

import {
  useAlertRuleFormQuery,
  AlertRuleFormQuery,
} from "./gql/Query.generated";
import {
  useCreateAlertRuleMutation,
  useUpdateAlertRuleMutation,
  useDeleteAlertRuleMutation,
} from "./gql/Mutation.generated";

type ServerOption =
  AlertRuleFormQuery["getOrganizationDetails"]["servers"][number];
type DestinationOption = AlertRuleFormQuery["getAlertDestinations"][number];

const ALL_CHECK_KINDS = Object.keys(CHECK_INFO);

// Each option is a fixed-height single line and the list padding is zeroed so the
// menu shows exactly DEST_OPTION_ROWS rows (see maxMenuHeight below), the trailing
// half-row hinting that it scrolls.
const DEST_OPTION_HEIGHT = 44;
const DEST_OPTION_ROWS = 4.5;
const DESTINATION_SELECT_STYLES: StylesConfig = {
  ...reactSelectStyles,
  menuList: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
  option: (base) => ({
    ...base,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    height: DEST_OPTION_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: "transparent",
    ":hover": { backgroundColor: "#f5f5f7" },
    ":active": { backgroundColor: "#eaeaef" },
  }),
};

// Checks grouped by category (the "Category:" prefix on each check title),
// with the category name pulled out for a section heading.
const CHECK_CATEGORIES: {
  category: string;
  checks: {
    shorthand: string;
    shortTitle: string;
    group: string;
    name: string;
  }[];
}[] = (() => {
  const byCategory = new Map<
    string,
    { shorthand: string; shortTitle: string; group: string; name: string }[]
  >();
  Object.entries(CHECK_INFO).forEach(([shorthand, info]) => {
    const [category, ...rest] = info.title.split(": ");
    const [group, name] = shorthand.split("/");
    const entry = {
      shorthand,
      shortTitle: rest.length ? rest.join(": ") : category,
      group,
      name,
    };
    const list = byCategory.get(category) ?? [];
    list.push(entry);
    byCategory.set(category, list);
  });
  return Array.from(byCategory.entries())
    .map(([category, checks]) => ({
      category,
      checks: checks.sort((a, b) => a.shortTitle.localeCompare(b.shortTitle)),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
})();

function AlertRuleDetail({
  organizationSlug,
  alertRuleId,
  serverId,
}: {
  organizationSlug: string;
  alertRuleId?: string;
  // When creating a rule from a server's context, prefill that server.
  serverId?: string;
}) {
  const isEdit = !!alertRuleId && alertRuleId !== "new";
  const { data, loading, error, refetch } = useAlertRuleFormQuery({
    variables: { organizationSlug, alertRuleId: alertRuleId ?? "", isEdit },
  });

  if (loading || error || !data) {
    return <Loading error={!!error} />;
  }

  const detail = data.getAlertRuleDetails;
  return (
    <Form
      key={detail?.id ?? "new"}
      organizationSlug={organizationSlug}
      alertRuleId={isEdit ? alertRuleId : undefined}
      servers={data.getOrganizationDetails.servers}
      allDestinations={data.getAlertDestinations}
      initialName={detail?.name ?? ""}
      initialServerId={
        isEdit ? detail?.server?.humanId ?? null : serverId ?? null
      }
      initialCheckKinds={detail ? detail.checkKinds : ALL_CHECK_KINDS}
      initialDestinationIds={detail ? detail.destinations.map((d) => d.id) : []}
      canDelete={detail?.permittedToDelete ?? false}
      refetchDestinations={refetch}
    />
  );
}

function Form({
  organizationSlug,
  alertRuleId,
  servers,
  allDestinations,
  initialName,
  initialServerId,
  initialCheckKinds,
  initialDestinationIds,
  canDelete,
  refetchDestinations,
}: {
  organizationSlug: string;
  alertRuleId?: string;
  servers: ServerOption[];
  allDestinations: DestinationOption[];
  initialName: string;
  // null = org-wide rule (no server); a humanId string = that server.
  initialServerId: string | null;
  initialCheckKinds: string[];
  initialDestinationIds: string[];
  canDelete: boolean;
  refetchDestinations: () => Promise<unknown>;
}) {
  const { organizationAlertRules } = useRoutes();
  const goBack = useGoBackOr(organizationAlertRules(organizationSlug));
  const [name, setName] = useState(initialName);
  const [serverId, setServerId] = useState(initialServerId);
  const [checkKinds, setCheckKinds] = useState<string[]>(initialCheckKinds);
  const [destinationIds, setDestinationIds] = useState<string[]>(
    initialDestinationIds,
  );
  // Destination modal: null (closed), "new" (create), or a destination id (edit).
  const [destModal, setDestModal] = useState<string | null>(null);

  const [createRule, createResult] = useCreateAlertRuleMutation({
    update: evictRuleQueries,
  });
  const [updateRule, updateResult] = useUpdateAlertRuleMutation({
    update: evictRuleQueries,
  });
  const [deleteRule, deleteResult] = useDeleteAlertRuleMutation({
    update: evictRuleQueries,
  });

  const saveResult = alertRuleId ? updateResult : createResult;
  useAsyncActionFlash({
    called: saveResult.called,
    loading: saveResult.loading,
    error: saveResult.error?.message,
    success: "Alert rule saved",
    successRedirect: saveResult.data
      ? organizationAlertRules(organizationSlug)
      : undefined,
  });
  useAsyncActionFlash({
    called: deleteResult.called,
    loading: deleteResult.loading,
    error: deleteResult.error?.message,
    success: "Alert rule deleted",
    successRedirect: deleteResult.data
      ? organizationAlertRules(organizationSlug)
      : undefined,
  });

  function toggleDestination(id: string, enabled: boolean) {
    setDestinationIds((prev) =>
      enabled ? [...prev, id] : prev.filter((d) => d !== id),
    );
  }

  function toggleCheck(checkKind: string, enabled: boolean) {
    setCheckKinds((prev) =>
      enabled
        ? [...prev, checkKind]
        : prev.filter((kind) => kind !== checkKind),
    );
  }

  // Split the full destination list into selected (shown as removable chips) and
  // the rest (offered in the searchable "Add a destination" picker), preserving
  // the query's name order in both.
  const selectedDestinations = allDestinations.filter((d) =>
    destinationIds.includes(d.id),
  );
  const unselectedDestinations = allDestinations.filter(
    (d) => !destinationIds.includes(d.id),
  );

  const saving = saveResult.loading || deleteResult.loading;
  // When editing, disable Save until something actually changes.
  const unchanged =
    !!alertRuleId &&
    name === initialName &&
    serverId === initialServerId &&
    sameSet(checkKinds, initialCheckKinds) &&
    sameSet(destinationIds, initialDestinationIds);
  const saveDisabledReason =
    name.trim() === ""
      ? "Enter a name for this rule"
      : checkKinds.length === 0
      ? "Select at least one check"
      : unchanged
      ? "No changes to save"
      : undefined;

  function handleSave() {
    // A rule with no destinations is valid but niche — its alerts go nowhere — so
    // confirm before saving one.
    if (
      destinationIds.length === 0 &&
      !window.confirm(
        "This alert rule has no destinations, so its alerts won't be delivered anywhere. Save anyway?",
      )
    ) {
      return;
    }
    const vars = { name, serverId, checkKinds, destinationIds };
    if (alertRuleId) {
      updateRule({ variables: { alertRuleId, ...vars } });
    } else {
      createRule({ variables: { organizationSlug, ...vars } });
    }
  }

  function handleDelete() {
    if (alertRuleId && window.confirm("Delete this alert rule?")) {
      deleteRule({ variables: { alertRuleId } });
    }
  }

  async function handleCreatedDestination(dest: { id: string }) {
    await refetchDestinations();
    setDestinationIds((prev) =>
      prev.includes(dest.id) ? prev : [...prev, dest.id],
    );
    setDestModal(null);
  }

  async function handleSavedDestination() {
    await refetchDestinations();
    setDestModal(null);
  }

  return (
    <>
      <Panel title={alertRuleId ? "Edit Alert Rule" : "New Alert Rule"}>
        <PanelSection>
          <label className="flex flex-col font-normal">
            <SectionHeading>
              Name <span className="text-[#c00]">*</span>
            </SectionHeading>
            <input
              className="form-control max-w-[600px]"
              required
              aria-required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </label>

          <label className="flex flex-col mt-4 font-normal">
            <SectionHeading>Server</SectionHeading>
            <select
              className="form-control max-w-[600px]"
              value={serverId ?? ""}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setServerId(value === "" ? null : value);
              }}
            >
              <option value="">All servers</option>
              {servers.map((s) => (
                <option key={s.humanId} value={s.humanId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <div className="form-group mt-4">
            <SectionHeading>Checks</SectionHeading>
            <div>
              Checks will be triggered based on the per-server check
              configuration that defines the thresholds, or the default if not
              configured.{" "}
              <a href="https://pganalyze.com/docs/checks" target="_blank">
                Learn more
              </a>
              .
            </div>
            {CHECK_CATEGORIES.map(({ category, checks }) => (
              <div key={category} className="mt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#999] border-b border-[#e8e8ee] pb-1 mb-1">
                  {category}
                </div>
                {checks.map((check) => {
                  const severity = checkMaxSeverity(check.group, check.name);
                  const description = getCheckDescription(
                    check.group,
                    check.name,
                  );
                  const checkId = `check-${check.shorthand}`;
                  return (
                    <div key={check.shorthand} className="group py-1">
                      <div className="flex items-center gap-2">
                        <input
                          id={checkId}
                          type="checkbox"
                          className="h-4 w-4 !mt-0 cursor-pointer"
                          checked={checkKinds.includes(check.shorthand)}
                          onChange={(e) =>
                            toggleCheck(
                              check.shorthand,
                              e.currentTarget.checked,
                            )
                          }
                        />
                        <label
                          htmlFor={checkId}
                          className="!mb-0 cursor-pointer font-medium"
                        >
                          {check.shortTitle}
                        </label>
                        {severity && (
                          <CheckStatusBadge
                            status={severity}
                            className="!ml-0 inline-flex items-center gap-1 leading-none !py-1"
                          />
                        )}
                      </div>
                      {description && (
                        <div className="ml-6 text-[12px] text-[#606771]">
                          {firstSentence(description)}{" "}
                          <a
                            href={`https://pganalyze.com/docs/checks/${check.group}/${check.name}`}
                            target="_blank"
                          >
                            Learn more
                          </a>
                          .
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="form-group mt-4">
            <SectionHeading>Destinations</SectionHeading>
            {selectedDestinations.length === 0 ? (
              <div className="mb-1 text-[13px] text-[#606771]">
                No destinations selected: alerts for the checks above won&apos;t
                be sent anywhere.
              </div>
            ) : (
              <div className="mb-1 flex flex-wrap">
                {selectedDestinations.map((dest) => (
                  <SelectedDestinationChip
                    key={dest.id}
                    name={dest.name}
                    onEdit={() => setDestModal(dest.id)}
                    onRemove={() => toggleDestination(dest.id, false)}
                  />
                ))}
              </div>
            )}
            <SingleSelect<DestinationOption>
              className="mt-2 max-w-[600px]"
              value={null}
              options={unselectedDestinations}
              onChange={(dest) => dest && toggleDestination(dest.id, true)}
              getOptionValue={(dest) => dest.id}
              getOptionLabel={(dest) => dest.name}
              formatOptionLabel={(dest) => (
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="shrink-0">{dest.name}</span>
                  {/* Target chips stay on one line; the menu width clips any
                      overflow rather than wrapping. */}
                  <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                    {dest.integrations.map((i) => (
                      <TargetTag
                        key={i.id}
                        mechanism={i.kind}
                        label={i.displayName}
                      />
                    ))}
                    {dest.memberCount > 0 && (
                      <TargetTag
                        mechanism="email"
                        label={pluralize("member", dest.memberCount, true)}
                      />
                    )}
                  </div>
                </div>
              )}
              placeholder="Add a destination…"
              noOptionsMessage={() =>
                allDestinations.length === 0
                  ? "No alert destinations yet"
                  : "All destinations added"
              }
              styles={DESTINATION_SELECT_STYLES}
              maxMenuHeight={DEST_OPTION_HEIGHT * DEST_OPTION_ROWS}
              footer={
                <button
                  type="button"
                  // onMouseDown fires before the input blur that would close the menu.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDestModal("new");
                  }}
                  // Match the option row height so the footer reads as one more row.
                  style={{ height: DEST_OPTION_HEIGHT }}
                  className="flex w-full cursor-pointer items-center gap-1 border-t border-[#e8e8ee] px-3 text-left text-sm hover:bg-[#f5f5f7]"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
                  Create new destination
                </button>
              }
              aria-label="Add a destination"
            />
          </div>

          <span
            title={saveDisabledReason}
            className={
              saveDisabledReason
                ? "inline-block cursor-not-allowed"
                : "inline-block"
            }
          >
            <Button
              bare
              className={classNames(
                "btn btn-success",
                saveDisabledReason && "pointer-events-none",
              )}
              disabled={!!saveDisabledReason || saving}
              onClick={handleSave}
            >
              Save alert rule
            </Button>
          </span>
          <Button
            bare
            className="text-link hover:text-link-hover ml-3"
            disabled={saving}
            onClick={goBack}
          >
            Cancel
          </Button>
        </PanelSection>
        {destModal !== null && (
          <ModalContainer
            title={
              destModal === "new"
                ? "Add Alert Destination"
                : "Edit Alert Destination"
            }
            layout="right sidebar"
            width="40rem"
            onClose={() => setDestModal(null)}
          >
            <AlertDestinationDetailModal
              organizationSlug={organizationSlug}
              destinationId={destModal === "new" ? undefined : destModal}
              onCreated={(dest) => void handleCreatedDestination(dest)}
              onSaved={() => void handleSavedDestination()}
              onCancel={() => setDestModal(null)}
            />
          </ModalContainer>
        )}
      </Panel>
      {canDelete && (
        <Panel title="Removal">
          <PanelSection>
            <p>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              Deleting this alert rule stops delivering its checks&apos; alerts
              to the selected destinations. The destinations themselves are not
              deleted.
            </p>
            <p>
              <span className="block form-group">
                <button
                  className="btn btn-danger"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  Delete Alert Rule
                </button>
              </span>
            </p>
          </PanelSection>
        </Panel>
      )}
    </>
  );
}

// A selected destination in the rule editor: a removable name chip that opens the
// destination editor when clicked. Name-only (no mechanism icons) so it reads
// distinctly from the icon-bearing target chips in the picker below.
function SelectedDestinationChip({
  name,
  onEdit,
  onRemove,
}: {
  name: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <span className="mr-2 mt-[5px] inline-flex items-stretch rounded border border-[#ddd] bg-white align-middle text-[12px]">
      <button
        type="button"
        onClick={onEdit}
        title={`Edit ${name}`}
        className="inline-flex min-w-0 items-center rounded-l px-[6px] py-[2px] text-inherit hover:bg-[#f5f5f7]"
      >
        <span className="truncate text-sm">{name}</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        className="inline-flex shrink-0 items-center border-l border-[#ddd] px-[6px] text-[#999] hover:text-[#c00]"
      >
        ×
      </button>
    </span>
  );
}

// Drop cached rule-derived queries after a rule changes so the list (and the
// per-check view) refetch fresh on next mount instead of showing a stale cache.
function evictRuleQueries(cache: ApolloCache<object>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAlertRules" });
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAlertCheckConfigs" });
  cache.gc();
}

// Check descriptions are HTML and can span multiple sentences/paragraphs; for
// the rule editor we show just the first sentence as plain text.
function firstSentence(html: string): string {
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const match = text.match(/^.*?[.!?](?=\s|$)/);
  return match ? match[0] : text;
}

export default AlertRuleDetail;
